/**
 * CashFlowTrendChart Component
 *
 * Multi-month revenue vs cost (realized) bars with a net cashflow line,
 * rendered with Recharts. Periods come from /report/cash-flow/trend.
 */

import React, { useMemo } from 'react';
import { Box, Text, Skeleton, Center } from '@chakra-ui/react';
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Line,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from 'recharts';
import { CashFlowTrendPoint, ChartStyle } from '../types';
import { formatCompact, formatNumber, formatPeriodeShort } from '../utils';

const REV_COLOR = '#1E8A4E';
const COST_COLOR = '#BD4A30';
const NET_COLOR = '#C2992F';

interface CashFlowTrendChartProps {
    trend: CashFlowTrendPoint[];
    loading: boolean;
    chartStyle?: ChartStyle;
}

interface ChartDatum {
    periode: string;
    label: string;
    revenue: number;
    cost: number;
    net: number;
}

const CustomTooltip: React.FC<any> = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
        <Box bg="white" p={3} borderRadius="md" boxShadow="md" border="1px" borderColor="gray.200">
            <Text fontWeight="bold" fontSize="sm" mb={1}>{label}</Text>
            {payload.map((entry: any) => (
                <Text key={entry.dataKey} fontSize="xs" color={entry.color} fontFamily="mono">
                    {entry.name}: {formatNumber(entry.value)}
                </Text>
            ))}
        </Box>
    );
};

const CashFlowTrendChart: React.FC<CashFlowTrendChartProps> = ({ trend, loading, chartStyle = 'bars' }) => {
    const data: ChartDatum[] = useMemo(
        () =>
            trend.map((t) => ({
                periode: t.periode,
                label: formatPeriodeShort(t.periode),
                revenue: t.total_revenue_realized,
                cost: t.total_cost_realized,
                net: t.net_realized,
            })),
        [trend],
    );

    const renderChart = (): React.ReactNode => {
        if (loading) {
            return <Skeleton height="300px" borderRadius="lg" />;
        }

        if (data.length === 0) {
            return (
                <Center height="300px">
                    <Text color="gray.400" fontSize="sm">Tidak ada data tren</Text>
                </Center>
            );
        }

        const isLines = chartStyle === 'lines';
        const isArea = chartStyle === 'area';

        return (
            <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={data} margin={{ top: 10, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E3D6" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={formatCompact} tick={{ fontSize: 11 }} width={64} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {isArea ? (
                        <>
                            <Area dataKey="revenue" name="Revenue" type="monotone" fill={REV_COLOR} stroke={REV_COLOR} fillOpacity={0.25} strokeWidth={2} dot={false} />
                            <Area dataKey="cost" name="Cost" type="monotone" fill={COST_COLOR} stroke={COST_COLOR} fillOpacity={0.25} strokeWidth={2} dot={false} />
                        </>
                    ) : isLines ? (
                        <>
                            <Line dataKey="revenue" name="Revenue" type="monotone" stroke={REV_COLOR} strokeWidth={2} dot={{ r: 3 }} />
                            <Line dataKey="cost" name="Cost" type="monotone" stroke={COST_COLOR} strokeWidth={2} dot={{ r: 3 }} />
                        </>
                    ) : (
                        <>
                            <Bar dataKey="revenue" name="Revenue" fill={REV_COLOR} radius={[3, 3, 0, 0]} maxBarSize={28} />
                            <Bar dataKey="cost" name="Cost" fill={COST_COLOR} radius={[3, 3, 0, 0]} maxBarSize={28} />
                        </>
                    )}
                    <Line dataKey="net" name="Net" type="monotone" stroke={NET_COLOR} strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
            </ResponsiveContainer>
        );
    };

    return (
        <Box bg="white" p={{ base: 4, md: 6 }} borderRadius="xl" boxShadow="sm" border="1px" borderColor="gray.100">
            <Text fontSize="md" fontWeight="bold" color="gray.700" mb={4}>
                Tren Cash Flow (Realized)
            </Text>
            {renderChart()}
        </Box>
    );
};

export default CashFlowTrendChart;
